import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ScrollReveal from './ScrollReveal.jsx';

const CARD_STYLES = [
  {
    iconWrap: 'bg-violet-100 ring-violet-300/60',
    iconClass: 'text-[#7F22FE]',
    cta: 'bg-violet-600 text-white hover:bg-violet-700',
    cardHover: 'hover:border-violet-300/80 hover:shadow-[0_12px_40px_rgba(127,34,254,0.18)]',
  },
  {
    iconWrap: 'bg-green-100 ring-[#1acb12]/50',
    iconClass: 'text-[#00A63E]',
    cta: 'bg-[#0d2b7f] text-white hover:bg-[#0a2266]',
    cardHover: 'hover:border-[#1acb12]/70 hover:shadow-[0_12px_40px_rgba(26,203,18,0.2)]',
  },
  {
    iconWrap: 'bg-orange-100 ring-orange-300/70',
    iconClass: 'text-[#F54900]',
    cta: 'bg-[#F54900] text-white hover:bg-[#d93d00]',
    cardHover: 'hover:border-orange-300/80 hover:shadow-[0_12px_40px_rgba(245,73,0,0.18)]',
  },
];

const linkWrapClass =
  'group flex h-full min-h-0 flex-col items-center justify-center rounded-2xl border-2 border-slate-200/90 bg-white px-4 py-5 no-underline text-inherit shadow-sm outline-none transition duration-300 hover:-translate-y-1 focus-visible:ring-2 focus-visible:ring-[#1acb12] focus-visible:ring-offset-2 focus-visible:ring-offset-white sm:px-5 sm:py-6';

function HighlightCardLink({ href, external, cardHover, children }) {
  const className = `${linkWrapClass} ${cardHover}`;

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  if (href.startsWith('#')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}

export default function HighlightsGrid({ items }) {
  return (
    <section className="relative overflow-hidden border-y border-slate-200 bg-white py-10 lg:py-12">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute left-1/2 top-0 size-[min(100%,520px)] -translate-x-1/2 rounded-full bg-[#FBFFE1]/70 blur-3xl md:blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 lg:px-6">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {items.map((item, i) => {
            const style = CARD_STYLES[i % CARD_STYLES.length];
            const Icon = item.icon;
            return (
              <ScrollReveal key={item.title} delayMs={i * 75} className="min-w-0">
                <HighlightCardLink href={item.href} external={item.external} cardHover={style.cardHover}>
                  <div
                    className={`flex aspect-square w-20 items-center justify-center rounded-full p-5 ring-2 shadow-lg transition duration-300 group-hover:scale-110 group-hover:shadow-xl sm:w-[5.25rem] sm:p-6 ${style.iconWrap}`}
                  >
                    <Icon
                      className={`h-9 w-9 sm:h-10 sm:w-10 ${style.iconClass}`}
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </div>
                  <div className="mt-4 space-y-2 text-center">
                    <h3 className="font-display text-lg font-bold leading-snug text-[#0d2b7f] sm:text-xl">
                      {item.title}
                    </h3>
                    <p className="text-base leading-relaxed text-slate-600 sm:text-lg">{item.text}</p>
                    {item.buttonText ? (
                      <span
                        className={`mt-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold shadow-md transition duration-300 group-hover:gap-2 sm:text-base ${style.cta}`}
                      >
                        {item.buttonText.replace(/\s*→\s*$/, '')}
                        <ArrowRight
                          className="h-4 w-4 shrink-0 transition duration-300 group-hover:translate-x-0.5 sm:h-5 sm:w-5"
                          aria-hidden
                        />
                      </span>
                    ) : null}
                  </div>
                </HighlightCardLink>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
